import { convertSourcePointToDocument, normalizeCoordinateSystem } from "../geometry/coordinates.js";

export class DataManager {
  constructor({ store, exporter, importer, getCoordinateConfig = () => ({ unit: "m", scale: 1, displayUnit: "m" }) }) {
    this.store = store;
    this.exporter = exporter;
    this.importer = importer;
    this.getCoordinateConfig = getCoordinateConfig;
  }

  save() {
    return this.export("json");
  }

  load(payload) {
    const document = typeof payload === "string" ? JSON.parse(payload) : payload;
    return this.store.setDocument(document);
  }

  listDots() {
    return this.store.getDocument().dots;
  }

  listObjects() {
    return this.store.getDocument().objects;
  }

  addDot(dot) {
    return this.store.addDot(dot);
  }

  addObject(object) {
    this.assertObjectReferences(object);
    return this.store.addObject(object);
  }

  removeObject(objectId) {
    return this.store.removeObject(objectId);
  }

  replaceObject(objectId, replacementObjects) {
    const objects = Array.isArray(replacementObjects) ? replacementObjects : [];

    for (const object of objects) {
      this.assertObjectReferences(object);
    }

    const replacedObjects = this.store.replaceObject(objectId, objects);

    if (!replacedObjects) {
      throw new Error(`Unknown object: ${objectId}`);
    }

    return replacedObjects;
  }

  createUniqueDotId(prefix = "dot") {
    return createUniqueId(prefix, this.store.getDocument().dots.map((dot) => dot.id));
  }

  createUniqueObjectId(prefix = "object") {
    return createUniqueId(prefix, this.store.getDocument().objects.map((object) => object.id));
  }

  getResolvedObjects() {
    const document = this.store.getDocument();
    const dotsById = new Map(document.dots.map((dot) => [dot.id, dot]));

    return document.objects.map((object) => ({
      ...object,
      dots: object.points.map((pointId) => dotsById.get(pointId) ?? null)
    }));
  }

  export(format) {
    return this.exporter.export(format, this.store.getDocument(), {
      coordinates: normalizeCoordinateSystem(this.getCoordinateConfig())
    });
  }

  import(format, payload, options = {}) {
    const document = this.importer.import(format, payload);
    const convertedDocument = maybeConvertImportedDocument(document, {
      targetCoordinates: this.getCoordinateConfig(),
      sourceCoordinates: options.sourceCoordinates ?? options.source,
      convertCoordinates: options.convertCoordinates
    });

    return this.store.setDocument(convertedDocument);
  }

  assertObjectReferences(object) {
    const points = Array.isArray(object.points) ? object.points : object.dotIds;

    if (!Array.isArray(points) || points.length === 0) {
      throw new Error("Objects must define a non-empty points array.");
    }

    for (const pointId of points) {
      if (!this.store.getDot(pointId)) {
        throw new Error(`Unknown point reference: ${pointId}`);
      }
    }
  }
}

function createUniqueId(prefix, existingIds) {
  const ids = new Set(existingIds);
  let suffix = 1;
  let candidate = `${prefix}-${suffix}`;

  while (ids.has(candidate)) {
    suffix += 1;
    candidate = `${prefix}-${suffix}`;
  }

  return candidate;
}

function maybeConvertImportedDocument(document, options) {
  const targetCoordinates = normalizeCoordinateSystem(options.targetCoordinates);
  const declaredSource = options.sourceCoordinates ?? getDocumentCoordinateMetadata(document);
  const shouldConvert = options.convertCoordinates ?? Boolean(declaredSource);

  if (!shouldConvert) {
    return document;
  }

  if (!declaredSource) {
    throw new Error("Import coordinate conversion requires declared source coordinates with unit and scale.");
  }

  const sourceCoordinates = normalizeCoordinateSystem(declaredSource);
  const dots = Array.isArray(document.dots)
    ? document.dots.map((dot) => convertSourcePointToDocument(dot, sourceCoordinates, targetCoordinates))
    : [];

  return {
    ...document,
    metadata: {
      ...(document.metadata ?? {}),
      coordinates: targetCoordinates,
      sourceCoordinates
    },
    dots
  };
}

function getDocumentCoordinateMetadata(document) {
  return document?.metadata?.coordinates ?? document?.settings?.coordinates ?? null;
}
